// Step 3 — Project Setup: project-level inputs, then the fake "Calculate" job.
import { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calculator, ClipboardList, Settings2 } from "lucide-react";
import { AuthContext } from "context/AuthContext";
import { useAlert } from "context/AlertContext";
import useBackgroundJob from "../../hooks/useBackgroundJob";
import { CALC_STAGES, startCalculation } from "../../mock/estimateService.mock";
import { BRAND_MODES, CONDITIONS } from "../../mock/config.mock";
import { buildEstimateLine, indexConfig } from "../../engine/calcMock";
import { Button, Card, CardHeader, Field, NumberInput, Segmented, TextInput, YES_NO } from "../../components/ui";
import { JobProgress } from "../../components/PageStates";

const CONDITION_OPTIONS = CONDITIONS.map((c) => ({ value: c, label: c }));

const isNum = (v) => v !== "" && v != null && Number.isFinite(Number(v));

export const validateProject = (p) => {
  const e = {};
  ["projectName", "location", "state", "clientGc", "estimator", "bidNumber", "bidDate"].forEach((k) => {
    if (!String(p[k] ?? "").trim()) e[k] = "Required";
  });
  ["interiorCrew", "exteriorCrew"].forEach((k) => {
    if (!isNum(p[k]) || !Number.isInteger(Number(p[k])) || p[k] < 1 || p[k] > 30) e[k] = "Whole number, 1–30";
  });
  if (!isNum(p.difficultyFactor) || p.difficultyFactor < 0.5 || p.difficultyFactor > 3) e.difficultyFactor = "Between 0.50 and 3.00";
  if (!isNum(p.contingencyPct) || p.contingencyPct < 0 || p.contingencyPct > 0.25) e.contingencyPct = "Between 0% and 25%";
  if (!isNum(p.projectInsurance) || p.projectInsurance < 0) e.projectInsurance = "Must be 0 or more";
  return e;
};

const toNumbers = (p) => ({
  ...p,
  interiorCrew: Number(p.interiorCrew),
  exteriorCrew: Number(p.exteriorCrew),
  difficultyFactor: Number(p.difficultyFactor),
  contingencyPct: Number(p.contingencyPct),
  projectInsurance: Number(p.projectInsurance),
});

function SummaryRow({ label, value, tone = "text-text_primary" }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-[12.5px]">
      <span className="text-text_secondary">{label}</span>
      <span className={`font-semibold tabular-nums ${tone}`}>{value}</span>
    </div>
  );
}

export default function StepProjectSetup({ jobId, project, setProject, config, lines, mappings, onBack }) {
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const bg = useBackgroundJob();
  const [submitted, setSubmitted] = useState(false);

  const errors = validateProject(project);
  // Required-field errors only after the first submit; range errors immediately.
  const shown = (k) => (submitted || errors[k] !== "Required" ? errors[k] : undefined);
  const set = (k) => (v) => setProject({ [k]: v });
  const text = (k) => ({ id: `ps-${k}`, value: project[k] ?? "", onChange: (e) => setProject({ [k]: e.target.value }), hasError: !!shown(k) });

  const mappedLines = useMemo(() => lines.filter((l) => mappings[l.id]?.surfaceId), [lines, mappings]);
  const skipped = lines.length - mappedLines.length;
  const noQty = mappedLines.filter((l) => !(l.qty > 0)).length;

  const handleCalculate = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length) {
      showAlert({ type: "warning", message: "Fix the highlighted fields before calculating." });
      return;
    }
    const clean = toNumbers(project);
    const idx = indexConfig(config);
    const estLines = mappedLines.map((l) => buildEstimateLine(l, mappings[l.id].surfaceId, config, clean, idx));
    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || clean.estimator;
    try {
      const result = await bg.run(() => startCalculation({ jobId: Number(jobId), project: clean, lines: estLines, user: userName }));
      if (result?.estimateId) {
        showAlert({ type: "success", message: `Estimate ${result.estimateId} created` });
        navigate(`/swiftboard/estimates/${result.estimateId}/takeoff`);
      }
    } catch (e) {
      showAlert({ type: "error", message: e.message || "Calculation failed" });
    }
  };

  const locked = bg.running;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Card>
        <CardHeader icon={ClipboardList} title="Project setup" subtitle="Project-level inputs used by every line calculation" />
        <fieldset disabled={locked} className="grid grid-cols-1 gap-x-4 gap-y-3.5 p-4 md:grid-cols-2 xl:grid-cols-3">
          <Field label="Project Name" htmlFor="ps-projectName" required error={shown("projectName")} className="md:col-span-2 xl:col-span-3">
            <TextInput {...text("projectName")} />
          </Field>
          <Field label="Location" htmlFor="ps-location" required error={shown("location")}>
            <TextInput {...text("location")} />
          </Field>
          <Field label="State" htmlFor="ps-state" required error={shown("state")}>
            <TextInput {...text("state")} />
          </Field>
          <Field label="Client / GC" htmlFor="ps-clientGc" required error={shown("clientGc")}>
            <TextInput {...text("clientGc")} />
          </Field>
          <Field label="Estimator" htmlFor="ps-estimator" required error={shown("estimator")}>
            <TextInput {...text("estimator")} />
          </Field>
          <Field label="Bid #" htmlFor="ps-bidNumber" required error={shown("bidNumber")}>
            <TextInput {...text("bidNumber")} />
          </Field>
          <Field label="Bid Date" htmlFor="ps-bidDate" required error={shown("bidDate")}>
            <TextInput type="date" {...text("bidDate")} />
          </Field>

          <Field label="Tax Exempt">
            <Segmented options={YES_NO} value={project.taxExempt} onChange={set("taxExempt")} ariaLabel="Tax exempt" disabled={locked} />
          </Field>
          <Field label="Bond Required" hint="Adds Bonds & Insurance (2%) to the bid">
            <Segmented options={YES_NO} value={project.bondRequired} onChange={set("bondRequired")} ariaLabel="Bond required" disabled={locked} />
          </Field>
          <Field label="Brand Mode" hint="Swaps products to SW / BM equivalents">
            <Segmented options={BRAND_MODES} value={project.brandMode} onChange={set("brandMode")} ariaLabel="Brand mode" disabled={locked} />
          </Field>

          <Field label="Condition Default" className="md:col-span-2" hint="Drives prep hours and the primer rule">
            <Segmented options={CONDITION_OPTIONS} value={project.conditionDefault} onChange={set("conditionDefault")} ariaLabel="Condition default" disabled={locked} />
          </Field>
          <Field label="Job Difficulty Factor" htmlFor="ps-difficultyFactor" error={shown("difficultyFactor")} hint="Multiplies all labor hours (1.00 = normal)">
            <NumberInput id="ps-difficultyFactor" step="0.05" value={project.difficultyFactor} onChange={set("difficultyFactor")} hasError={!!shown("difficultyFactor")} />
          </Field>

          <Field label="Interior Crew Size" htmlFor="ps-interiorCrew" error={shown("interiorCrew")}>
            <NumberInput id="ps-interiorCrew" step="1" value={project.interiorCrew} onChange={set("interiorCrew")} hasError={!!shown("interiorCrew")} />
          </Field>
          <Field label="Exterior Crew Size" htmlFor="ps-exteriorCrew" error={shown("exteriorCrew")}>
            <NumberInput id="ps-exteriorCrew" step="1" value={project.exteriorCrew} onChange={set("exteriorCrew")} hasError={!!shown("exteriorCrew")} />
          </Field>
          <Field label="Contingency %" htmlFor="ps-contingency" error={shown("contingencyPct")}>
            <NumberInput
              id="ps-contingency"
              step="0.5"
              value={project.contingencyPct === "" ? "" : +(Number(project.contingencyPct) * 100).toFixed(2)}
              onChange={(v) => setProject({ contingencyPct: v === "" ? "" : v / 100 })}
              hasError={!!shown("contingencyPct")}
            />
          </Field>
          <Field label="Project Insurance ($)" htmlFor="ps-insurance" error={shown("projectInsurance")} hint="Flat amount added before profit">
            <NumberInput id="ps-insurance" step="50" value={project.projectInsurance} onChange={set("projectInsurance")} hasError={!!shown("projectInsurance")} />
          </Field>
        </fieldset>
      </Card>

      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader icon={Settings2} title="Settings version" />
          <div className="flex flex-col gap-1.5 p-4">
            <p className="text-[13px] font-semibold text-text_primary">{config.versionLabel}</p>
            <p className="text-[11.5px] text-text_muted">Rates, productivity and products are locked to this version for the estimate.</p>
            <Link to="/swiftboard/estimate-settings/versions" className="text-[12px] font-medium text-primary hover:underline">
              View settings versions
            </Link>
          </div>
        </Card>

        <Card>
          <CardHeader icon={Calculator} title="Ready to calculate" />
          <div className="flex flex-col gap-3 p-4">
            <div className="divide-y divide-border_soft">
              <SummaryRow label="Lines to calculate" value={mappedLines.length} />
              <SummaryRow label="Skipped (unmapped)" value={skipped} tone={skipped ? "text-warning" : "text-text_primary"} />
              <SummaryRow label="Lines without quantity" value={noQty} tone={noQty ? "text-warning" : "text-text_primary"} />
            </div>
            {noQty > 0 && <p className="text-[11.5px] text-text_muted">Lines without a quantity are kept and flagged in the Takeoff grid for you to fill in.</p>}

            {bg.running || bg.progress?.done ? (
              <div className="rounded-[4px] border border-border p-3">
                <JobProgress progress={bg.progress} stages={CALC_STAGES} title="Calculating estimate…" />
              </div>
            ) : (
              <Button variant="accent" size="md" icon={Calculator} onClick={handleCalculate} disabled={mappedLines.length === 0} className="w-full">
                Calculate Estimate
              </Button>
            )}
            <Button variant="secondary" icon={ArrowLeft} onClick={onBack} disabled={locked} className="w-full">
              Back to Map Surfaces
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
